/**
 * XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX
 *
 * THIS FILE IS MANAGED CENTRALLY BY THE `common-code` REPO.
 * IT COULD BE AUTO-REPLACED AT ANY TIME.
 * DO NOT MAKE CUSTOM CHANGES TO THIS FILE.
 * @see https://gitlab.com/dea-aero/development/common-code
 *
 * XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX XXX
 */
import {useState, useEffect, useCallback, useMemo} from 'react'
import {DataGridPro, useGridApiRef} from '@mui/x-data-grid-pro';
import {
    GridToolbarDensitySelector,
    GridToolbarColumnsButton,
    GridToolbarExport,
    GridToolbarContainer,
    gridFilteredSortedRowIdsSelector,
    selectedGridRowsSelector,
} from '@mui/x-data-grid-pro';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import {formatDate} from "./Export";
import {minutesToTimeStr} from "../util/time";
import {useToastStore} from "../Toast";


dayjs.extend(utc);
dayjs.extend(timezone);

export const formatPerson = cell => cell?.email ? `${cell.name} <${cell.email}>` : '-';
export const renderPerson = cell => {
    const person = cell?.row[cell.field];
    if (!person?.name) return '-'
    const name = person.name.split(' ')
    return name[0][0] + ' ' + name[1]
}

export const dateGetter = cell => {
    return cell ? new Date(cell < 10000000000 ? cell * 1000 : cell) : null;
}
export const dateRenderer = cell => formatDate(cell.value);
export const bytesRenderer = cell => {
    if (cell.value === null) {
        return null
    }
    if (cell.value < 2000) {
        return `${cell.value} Bytes`
    }
    if (cell.value < 2000_000) {
        return `${cell.value >> 10} KiB`
    }
    return `${cell.value >> 20} MiB`
}

export const cellDefaults = {
    headerAlign: 'right',
    align: 'right',
    autosize: {
        includeOutliers: true,
        includeHeaders: true,
        expand: true,
    },
}

export const defaultDateCell = {
    ...cellDefaults,
    type: 'date',
    valueGetter: dateGetter,
    renderCell: dateRenderer,
    minWidth: 110,
}

export const defaultDateTimeCell = {
    ...cellDefaults,
    type: 'datetime',
    valueGetter: dateGetter,
    valueFormatter: (params) => dayjs(params.value).format('YYYY-MM-DDTHH:mm:ssZ'),
    minWidth: 110,
}

export const defaultBytesCell = {
    ...cellDefaults,
    type: 'number',
    valueFormatter: bytesRenderer,
    minWidth: 80,
}

export const defaultPersonCell = {
    ...cellDefaults,
    type: 'string',
    valueGetter: formatPerson,
    renderCell: renderPerson,
}

export const defaultMinutesCell = {
    ...cellDefaults,
    type: 'number',
    renderCell: minutesToTimeStr,
}

export const DataGrid = params => {
    const [wrapperStyle, setWrapperStyle] = useState({})
    const apiRef = useGridApiRef();
    const errorToast = useToastStore((state) => state.showError)

    const showExportButton = useMemo(() => {
        return !Boolean(params?.disableExport)
    }, [params?.disableExport])

    useEffect(() => {
        return setWrapperStyle(params.wrapperStyle || {height: 'calc(100dvh - 90px)'})
    }, [params?.wrapperStyle])

    const getSelectedRowsToExport = useCallback(({apiRef}) => {
        const selectedRowIds = selectedGridRowsSelector(apiRef);
        if (selectedRowIds.size > 0) {
            return Array.from(selectedRowIds.keys());
        }
        return gridFilteredSortedRowIdsSelector(apiRef);
    }, []);

    const {autosizeOptions} = params;
    useEffect(() => {
        if (autosizeOptions === null) return
        apiRef.current.autosizeColumns({
            includeHeaders: true,
            includeOutliers: true,
            expand: true,
        })
    }, [apiRef, autosizeOptions])

    const {scrollToRow} = params;
    useEffect(() => {
        if (typeof scrollToRow === 'undefined' || scrollToRow === null) return
        if (!apiRef.current) return;
        const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(scrollToRow);
        if (rowIndex === -1) return;
        apiRef.current.scrollToIndexes({
            rowIndex: rowIndex,
            colIndex: 0,
        });
    }, [apiRef, scrollToRow])

    const {toolbar} = params;

    const genToolbar = useCallback(() => <GridToolbarContainer>
        {showExportButton ? <GridToolbarExport printOptions={{disableToolbarButton: true}}/> : ''}
        <GridToolbarDensitySelector/>
        <GridToolbarColumnsButton/>
        {toolbar}
    </GridToolbarContainer>, [toolbar, showExportButton])

    const onProcessRowUpdateError = useCallback(e => {
        console.error('grid row update error', e)
        errorToast(e)
    }, [errorToast])

    const passthrough = {...params};
    delete passthrough?.toolbar;
    delete passthrough?.wrapperStyle;

    const [columns, setColumns] = useState(params.columns);

    // useEffect(() => setColumns(params.columns), [params.columns]);

    const handleColumnResize = useCallback(params => {
        const {field, width} = params;
        const cols = [...columns];
        const col = cols.find(col => col.field === field);
        if (col)
            col.width = width;
        setColumns(cols);
    }, [columns],);

    return <div style={wrapperStyle}>
        <DataGridPro
            apiRef={apiRef}
            sx={{mt: 0}}
            hideFooter={true}
            onColumnResize={handleColumnResize}
            slots={{toolbar: genToolbar}}
            slotProps={{
                toolbar: {printOptions: {getRowsToExport: getSelectedRowsToExport}},
            }}
            autosizeOptions={{
                includeOutliers: true,
                includeHeaders: true,
                expand: true,
            }}
            {...passthrough}
            onProcessRowUpdateError={onProcessRowUpdateError}
        />
    </div>
}
export default DataGrid;
